import { datadogRum } from '@datadog/browser-rum'
import { AuthContext } from 'context/AuthContext'
import UserContext from 'context/common/User/UserContext'
import { TWPERMISSIONS } from 'dataSource/auth/types'
import { useContext, useEffect } from 'react'

/**
 * Attaches the logged-in user's identity to Datadog RUM so frontend errors and
 * sessions are filterable by who hit them (e.g. a COE agent reporting a failed
 * booking). PII (email/name) in Datadog is approved — see THRIV-14035.
 *
 * Must be mounted inside both AuthContextWrapper and UserContextWrapper. The user
 * object (email/name) loads asynchronously after auth, so identity is set/enriched
 * reactively as fields become available. Logout clears identity via
 * `clearCredentials()` in hooks/useThriveAuth.
 */

// Most-specific / internal roles win, so support & clinician sessions are easy to
// isolate even when a user carries several permission groups.
const ROLE_PRIORITY: { permission: TWPERMISSIONS; role: string }[] = [
  { permission: TWPERMISSIONS.SUPPORT, role: 'support' },
  {
    permission: TWPERMISSIONS.THIRD_PARTY_SUPPORT,
    role: 'third_party_support',
  },
  { permission: TWPERMISSIONS.COE_STAFF, role: 'coe_staff' },
  { permission: TWPERMISSIONS.CLINICAL_ADMIN, role: 'clinical_admin' },
  { permission: TWPERMISSIONS.DEVOPS_ADMIN, role: 'devops_admin' },
  { permission: TWPERMISSIONS.SOFTWARE_DEVS, role: 'software_devs' },
  { permission: TWPERMISSIONS.CLINICIAN, role: 'clinician' },
  { permission: TWPERMISSIONS.PATIENT, role: 'patient' },
]

const derivePrimaryRole = (permissions: TWPERMISSIONS[]): string => {
  const match = ROLE_PRIORITY.find(({ permission }) =>
    permissions.includes(permission)
  )
  return match?.role ?? 'unknown'
}

const DatadogIdentity: React.FC = () => {
  const [authState] = useContext(AuthContext)
  const { user } = useContext(UserContext)

  const { isAuthenticated, permissions } = authState
  const { id, email, firstName, lastName } = user

  useEffect(() => {
    if (!isAuthenticated || !id) {
      return
    }

    const name = [firstName, lastName].filter(Boolean).join(' ') || undefined

    datadogRum.setUser({
      id,
      email,
      name,
      role: derivePrimaryRole(permissions),
      permissions,
    })
  }, [isAuthenticated, id, email, firstName, lastName, permissions])

  return null
}

export default DatadogIdentity
